import {Cctv, Bell, Flame, KeyRound, Network, ShieldCheck} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export interface Service {
  id: string
  slug: string
  icon: LucideIcon
  title: string
  shortDescription: string
  description: string
  image: string
  features: string[]
  caseStudy: {
    client: string
    challenge: string
    solution: string
    result: string
  }
}

export const services: Service[] = [
  {
    id: 'videovigilancia',
    slug: 'videovigilancia',
    icon: Cctv,
    title: 'Videovigilância',
    shortDescription: 'Câmeras IP, monitoramento 24/7 e análise inteligente de imagens.',
    description:
      'Projetamos e implementamos sistemas completos de videovigilância com câmeras IP de alta resolução, gravação em nuvem e local, e análise de vídeo com inteligência artificial para detecção de eventos em tempo real.',
    image: 'https://static.lumi.new/f8/f84b1aa3062eba5dfd3b9d499c836e20.png',
    features: [
      'Câmeras IP full HD e 4K com visão noturna',
      'Monitoramento remoto via aplicativo e web',
      'Análise inteligente: detecção de movimento e reconhecimento facial',
      'Armazenamento em nuvem e NVRs redundantes',
      'Integração com centrais de monitoramento 24/7',
    ],
    caseStudy: {
      client: 'Rede de Varejo Nortec',
      challenge: '32 lojas sem padronização de câmeras e sem monitoramento centralizado.',
      solution: 'Implantação de 480 câmeras IP com plataforma unificada de monitoramento em nuvem.',
      result: 'Redução de 68% em ocorrências de furto em 6 meses.',
    },
  },
  {
    id: 'alarmes-intrusao',
    slug: 'alarmes-intrusao',
    icon: Bell,
    title: 'Alarmes de Intrusão',
    shortDescription: 'Detecção perimetral e interna com resposta rápida a invasões.',
    description:
      'Soluções completas de alarme contra intrusão com sensores de movimento, barreiras perimetrais, sirenes e integração com centrais de monitoramento para resposta imediata a ocorrências.',
    image: 'https://static.lumi.new/99/99d282fc446a71b5d19cbe838e0129a8.png',
    features: [
      'Sensores de movimento infravermelho e dupla tecnologia',
      'Barreiras perimetrais e cercas inteligentes',
      'Central de alarme com comunicação GPRS/IP',
      'Notificações em tempo real via aplicativo',
      'Integração com equipes de resposta rápida',
    ],
    caseStudy: {
      client: 'Condomínio Prisma Corporate',
      challenge: 'Perímetro extenso com múltiplos pontos vulneráveis de invasão.',
      solution: 'Cercamento eletrônico perimetral integrado à central de alarme e videoverificação.',
      result: 'Zero invasões registradas desde a implantação, há 18 meses.',
    },
  },
  {
    id: 'alarme-incendio',
    slug: 'alarme-incendio',
    icon: Flame,
    title: 'Alarme de Incêndio',
    shortDescription: 'Detecção precoce e sistemas de combate a incêndio conforme normas.',
    description:
      'Sistemas de detecção e alarme de incêndio projetados conforme normas ABNT e corpo de bombeiros, com detectores de fumaça, calor e acionadores manuais integrados a centrais endereçáveis.',
    image: 'https://static.lumi.new/c6/c6d368513949e34fdd4e4e8619c7e435.png',
    features: [
      'Detectores de fumaça, calor e chama endereçáveis',
      'Centrais de alarme conforme NBR 17240',
      'Acionadores manuais e sirenes de emergência',
      'Integração com sistemas de combate a incêndio',
      'Laudos técnicos e AVCB para regularização',
    ],
    caseStudy: {
      client: 'Centro Logístico Aurora',
      challenge: 'Galpão de 12.000m² sem sistema de detecção conforme e AVCB pendente.',
      solution: 'Projeto e instalação de central endereçável com 210 pontos de detecção.',
      result: 'Aprovação total do AVCB e certificação emitida em 45 dias.',
    },
  },
  {
    id: 'controle-acesso',
    slug: 'controle-acesso',
    icon: KeyRound,
    title: 'Controle de Acesso',
    shortDescription: 'Gestão inteligente de entrada e permissões com biometria e cartões.',
    description:
      'Implementamos sistemas de controle de acesso físico com leitores biométricos, cartões de proximidade e reconhecimento facial, integrados à gestão de permissões e relatórios de acesso.',
    image: 'https://static.lumi.new/7e/7ea895769e90f745c75d967d28134565.png',
    features: [
      'Leitores biométricos e de reconhecimento facial',
      'Catracas, torniquetes e fechaduras eletrônicas',
      'Gestão de permissões por perfil e horário',
      'Relatórios de acesso e auditoria em tempo real',
      'Integração com RH e crachás corporativos',
    ],
    caseStudy: {
      client: 'Torre Empresarial Zenith',
      challenge: 'Controle manual de acesso gerando filas e falhas de segurança.',
      solution: 'Implantação de catracas biométricas integradas ao sistema de crachás.',
      result: 'Tempo médio de acesso reduzido em 75% e auditoria completa de fluxos.',
    },
  },
  {
    id: 'redes-telecom',
    slug: 'redes-telecom',
    icon: Network,
    title: 'Redes e Telecom',
    shortDescription: 'Infraestrutura de rede, cabeamento estruturado e conectividade corporativa.',
    description:
      'Projetamos e implementamos infraestrutura de redes e telecomunicações corporativas, incluindo cabeamento estruturado, Wi-Fi empresarial, links dedicados e integração de voz sobre IP.',
    image: 'https://static.lumi.new/95/95d9ad5b77eb3ec7b80dfb06a462da75.png',
    features: [
      'Cabeamento estruturado categoria 6/6A e fibra óptica',
      'Redes Wi-Fi empresarial de alta densidade',
      'Links dedicados e redundância de conectividade',
      'Telefonia VoIP e unificação de comunicação',
      'Monitoramento de rede e SLA de disponibilidade',
    ],
    caseStudy: {
      client: 'Hospital São Rafael',
      challenge: 'Rede legada instável afetando sistemas clínicos críticos.',
      solution: 'Redesenho completo da rede com redundância e monitoramento 24/7.',
      result: 'Disponibilidade de rede elevada para 99,98% em 12 meses.',
    },
  },
  {
    id: 'consultoria-integrada',
    slug: 'consultoria-integrada',
    icon: ShieldCheck,
    title: 'Consultoria Integrada de Segurança',
    shortDescription: 'Diagnóstico, projeto e gestão integrada de soluções de segurança.',
    description:
      'Nossa consultoria integrada avalia riscos, projeta arquiteturas de segurança física e digital, e gerencia a implantação de múltiplas soluções de forma coordenada, garantindo eficiência e compliance.',
    image: 'https://static.lumi.new/98/987d35af68794cb93a8a851ccff5d55e.png',
    features: [
      'Diagnóstico de vulnerabilidades e mapa de riscos',
      'Projeto executivo multidisciplinar',
      'Gestão de fornecedores e obras',
      'Compliance com normas técnicas e regulatórias',
      'Suporte contínuo e manutenção preventiva',
    ],
    caseStudy: {
      client: 'Grupo Industrial Metabras',
      challenge: 'Três plantas industriais com soluções de segurança desconexas.',
      solution: 'Unificação de videovigilância, controle de acesso e alarmes em plataforma única.',
      result: 'Redução de 40% nos custos operacionais de segurança.',
    },
  },
]
